import { preventSubmit, ValidatedSelect, ValidatedSelectItem } from '@cp-shared-19/frontend-ui';
import { Button, ButtonContainer, Fieldset, Form, Paragraph } from '@vwfs-bronson/bronson-react';
import { dashboardPagePath } from 'components/navigation/paths';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { EarlySettlementRequestDate, totalEarlySettlementValidationSchema } from 'cpfr-common';
import { getInitialValues } from './initialValues';
import { EmailInputModal } from '../../../../request/form-view/email-input/EmailInputModal';
import { getEarlySettlementReasons } from 'components/early-settlement/joint-entry-point/utils';

export type FormData = {
    requestDate: EarlySettlementRequestDate;
    reason: string;
    email?: string;
};

type TotalSettlementFormProps = {
    handleSubmit: (data: FormData) => void;
    errorCode?: string;
    customerType: string;
};

const availableMonths: ValidatedSelectItem[] = [
    { label: 'Janvier', value: 'JANUARY' },
    { label: 'Février', value: 'FEBRUARY' },
    { label: 'Mars', value: 'MARCH' },
    { label: 'Avril', value: 'APRIL' },
    { label: 'Mai', value: 'MAY' },
    { label: 'Juin', value: 'JUNE' },
    { label: 'Juillet', value: 'JULY' },
    { label: 'Août', value: 'AUGUST' },
    { label: 'Septembre', value: 'SEPTEMBER' },
    { label: 'Octobre', value: 'OCTOBER' },
    { label: 'Novembre', value: 'NOVEMBER' },
    { label: 'Décembre', value: 'DECEMBER' },
];

const translationPrefix = 'total-early-settlement.form';
const translationValidationPrefix = 'total-early-settlement.form.errors';
export const TotalEarlySettlementForm: React.FC<TotalSettlementFormProps> = ({
    handleSubmit,
    errorCode,
    customerType,
}) => {
    const { t } = useTranslation('early-settlement');
    const history = useHistory();
    const [error, setError] = useState<string | null>(errorCode || null);
    useEffect(() => {
        setError(errorCode || null);
    }, [errorCode]);
    const availableReasons: ValidatedSelectItem[] = getEarlySettlementReasons(customerType);
    const mandatoryFieldsInfo = t('joint-entry-point.mandatory-fields-info');
    const errorMessages = {
        requestDate: {
            required: t(`${translationValidationPrefix}.empty-date`),
        },
        reason: {
            required: t(`${translationValidationPrefix}.empty-reason`),
        },
        email: {
            required: t(`${translationValidationPrefix}.required`),
            mustBeEmail: t(`${translationValidationPrefix}.mustBeEmail`),
        },
    };

    return (
        <Formik
            initialValues={getInitialValues()}
            onSubmit={(data: FormData): void => {
                setError(null);
                handleSubmit(data);
            }}
            validationSchema={totalEarlySettlementValidationSchema(
                errorMessages,
                error === 'EMAIL_NOT_FOUND_OR_PROVIDED',
            )}
        >
            {(formik): JSX.Element => (
                <Form onSubmit={(e) => preventSubmit(e)} data-testid="total-settlement-form">
                    <Fieldset>
                        <Fieldset.Row>
                            <ValidatedSelect
                                aria-required={true}
                                required={true}
                                isMandatory={true}
                                className="c-form-field--no-floating"
                                name={'requestDate'}
                                label={t(`${translationPrefix}.request-date`)}
                                placeholder={t(`${translationPrefix}.request-date-placeholder`)}
                                selectItems={availableMonths}
                                requiredText=" *"
                            />
                        </Fieldset.Row>
                        <Fieldset.Row>
                            <ValidatedSelect
                                className="c-form-field--no-floating"
                                aria-required={false}
                                isMandatory={true}
                                required={true}
                                requiredText=" *"
                                name={'reason'}
                                label={t(`${translationPrefix}.reason`)}
                                placeholder={t(`${translationPrefix}.reason-placeholder`)}
                                selectItems={availableReasons.map(({ value, label }) => ({
                                    value,
                                    label,
                                }))}
                            />
                        </Fieldset.Row>
                        <Fieldset.Row>
                            <Paragraph>{t(`${translationPrefix}.reason-desc`)}</Paragraph>
                        </Fieldset.Row>
                        <Fieldset.Row>
                            <ButtonContainer center>
                                <Button
                                    secondary
                                    onClick={(): void => history.push(dashboardPagePath())}
                                    testId="cancelButton"
                                >
                                    {t('translation:editable-section-nav.cancel')}
                                </Button>
                                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                <Button onClick={formik.handleSubmit as any} type="button" testId="send">
                                    {t('translation:editable-section-nav.send')}
                                </Button>
                            </ButtonContainer>
                        </Fieldset.Row>
                    </Fieldset>
                    <p>{mandatoryFieldsInfo}</p>
                    <EmailInputModal
                        show={error === 'EMAIL_NOT_FOUND_OR_PROVIDED'}
                        onClose={() => setError(null)}
                        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
                        onConfirm={formik.handleSubmit as any}
                    />
                </Form>
            )}
        </Formik>
    );
};
